import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash, Archive, Lightbulb, Warning, MagicWand, FilmStrip, Prohibit, PaperPlaneTilt, Rocket, ArrowsClockwise } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { getStrengthBand } from "../components/NarrativeStrengthMeter";
import ShipPlatformDialog from "../components/ShipPlatformDialog";
import { useAuth } from "../lib/auth";

const TYPES = ["Carousel", "Founder Video", "Podcast Topic", "LinkedIn Post", "Tweet/Thread", "Ad Concept", "Instagram Caption"];
const STATUSES = ["Draft", "Review", "Approved", "In Production", "Published", "Performing", "Dismissed", "Archived"];
// Phase 5.5: the page now defaults to a virtual "active" tab that hides
// Dismissed / Archived / Performing. The real lifecycle statuses still
// drive the count tabs underneath when the user changes the filter.
const FILTER_OPTIONS = ["active", "Dismissed", "Archived", "all"];
const STATUS_DESCRIPTIONS = {
    "Draft": "Initial idea — generated or hand-written",
    "Review": "Being evaluated for strategic fit and narrative validity",
    "Approved": "Strategically validated and cleared for production",
    "In Production": "Actively being written, designed, filmed, or edited",
    "Published": "Deployed publicly",
    "Performing": "Connected to engagement/performance feedback (coming soon)",
    "Dismissed": "Intentionally removed from consideration — preserved as historical intelligence",
    "Archived": "Outdated, rejected, redundant, or no longer relevant",
};

const empty = { title: "", content_type: "Carousel", hook: "", core_insight: "", draft_copy: "", topic_tags: "" };

export default function ContentIdeas() {
    const { isAdmin } = useAuth();
    const [ideas, setIdeas] = useState([]);
    const [counts, setCounts] = useState({});
    const [activeStatus, setActiveStatus] = useState(
        // Phase 5.5: default to the new virtual "active" view so Dismissed
        // and Archived ideas drop out of the working set automatically.
        () => localStorage.getItem("haevn-ideas-tab") || "active"
    );
    const [filter, setFilter] = useState({ content_type: "" });
    const [topMode, setTopMode] = useState(true); // default to "Top 30" view
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => {
        const params = { status: activeStatus };
        if (filter.content_type) params.content_type = filter.content_type;
        if (topMode) params.top = true;
        const [r, c] = await Promise.all([
            api.get("/content-ideas/enriched", { params }),
            api.get("/content-ideas/counts"),
        ]);
        setIdeas(r.data?.ideas || []);
        setCounts(c.data);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const params = { status: activeStatus };
            if (filter.content_type) params.content_type = filter.content_type;
            if (topMode) params.top = true;
            try {
                const [r, c] = await Promise.all([
                    api.get("/content-ideas/enriched", { params }),
                    api.get("/content-ideas/counts"),
                ]);
                if (cancelled) return;
                setIdeas(r.data?.ideas || []);
                setCounts(c.data);
            } catch { /* surface elsewhere — render shows empty state */ }
        })();
        return () => { cancelled = true; };
    }, [activeStatus, filter, topMode]);

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title required");
            return;
        }
        const tags = (form.topic_tags || "").split(",").map((s) => s.trim()).filter(Boolean);
        const payload = { ...form, topic_tags: tags };
        delete payload.id;
        if (editing?.id) {
            await api.patch(`/content-ideas/${editing.id}`, payload);
            toast.success("Updated");
        } else {
            await api.post("/content-ideas", payload);
            toast.success("Idea added");
        }
        setOpen(false);
        setEditing(null);
        setForm(empty);
        await load();
    };

    const startEdit = (idea) => {
        setEditing(idea);
        setForm({
            title: idea.title,
            content_type: idea.content_type,
            hook: idea.hook,
            core_insight: idea.core_insight,
            draft_copy: idea.draft_copy,
            topic_tags: (idea.topic_tags || []).join(", "),
        });
        setOpen(true);
    };

    const updateStatus = async (id, status) => {
        await api.patch(`/content-ideas/${id}`, { status });
        await load();
    };
    const dup = async (id) => {
        await api.post(`/content-ideas/${id}/duplicate`);
        toast.success("Duplicated");
        await load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this idea?")) return;
        await api.delete(`/content-ideas/${id}`);
        await load();
    };

    const [regenBusy, setRegenBusy] = useState(null);
    const regenVoice = async (id) => {
        setRegenBusy(id);
        try {
            await api.post(`/content-ideas/${id}/regenerate-voice`, { intensity: "high" });
            toast.success("Regenerated with stronger HAEVN voice");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Regeneration failed");
        } finally {
            setRegenBusy(null);
        }
    };

    const [sendBusy, setSendBusy] = useState(null);
    const [shipBusy, setShipBusy] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkShipBusy, setBulkShipBusy] = useState(false);
    const [bulkArchiving, setBulkArchiving] = useState(false);
    const [bulkDismissing, setBulkDismissing] = useState(false);
    const [dupModal, setDupModal] = useState(null); // { idea_id, existing_content_id, existing_title }

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());
    const selectAllVisible = () => setSelected(new Set(ideas.map((i) => i.id)));

    const openExisting = (contentId) => {
        // Stash so Studio can scroll/highlight the row in the Queue tab.
        localStorage.setItem("haevn-cs-tab", "queue");
        localStorage.setItem("haevn-cs-highlight", contentId);
        window.location.href = "/content-studio";
    };

    // Phase 5.5: 'In Studio' badge → take the user to Content Studio,
    // filtered to the variants spawned from this idea. We stash an
    // idea_id filter that ContentStudio reads on mount.
    const openStudioForIdea = (ideaId) => {
        localStorage.setItem("haevn-cs-tab", "queue");
        localStorage.setItem("haevn-cs-idea-filter", ideaId);
        window.location.href = "/content-studio";
    };

    const sendToStudio = async (id, { force = false } = {}) => {
        setSendBusy(id);
        try {
            // Phase 19: "Send to Studio" now creates a Source (not a direct
            // ContentItem). The user then chooses which platforms the AI
            // should produce on the Source Detail page. `force` is kept
            // for backwards compat with the duplicate modal but ignored.
            void force;
            const r = await api.post(`/content-studio/sources/from-idea/${id}`);
            toast.success("Source created from idea", {
                description: `Open the source to choose platforms and generate assets.`,
                action: { label: "Open source", onClick: () => { window.location.href = `/content-studio/sources/${r.data.id}`; } },
                duration: 8000,
            });
            // Navigate straight into the source so the next action is obvious.
            window.location.href = `/content-studio/sources/${r.data.id}`;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to create source");
        } finally {
            setSendBusy(null);
        }
    };

    const bulkSendToStudio = async ({ force = false } = {}) => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        void force;
        setBulkBusy(true);
        // Phase 19: bulk send now creates N Sources (one per idea) in the
        // Sources tab. Users can open each one and pick platforms.
        try {
            const results = await Promise.allSettled(
                ids.map((id) => api.post(`/content-studio/sources/from-idea/${id}`))
            );
            const created = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.length - created;
            toast.success("Bulk send complete", {
                description: `${created} source${created === 1 ? "" : "s"} created${failed ? ` · ${failed} failed` : ""}. Open the Sources tab to choose platforms.`,
                action: created > 0
                    ? { label: "Open Sources", onClick: () => { localStorage.setItem("haevn-cs-tab", "sources"); window.location.href = "/content-studio"; } }
                    : undefined,
                duration: 10000,
            });
            clearSelection();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk send failed");
        } finally {
            setBulkBusy(false);
        }
    };

    // V2.9.1 — Async ship. The old sync ship exceeded Cloudflare's ~100s
    // edge timeout in prod (multi-LLM + Buffer chain). Now the backend
    // returns a job_id immediately and this handler polls
    // /ship-jobs/{job_id} until status ∈ {completed, failed}.
    //
    // V2.9.2 — Platform picker. Ship no longer POSTs immediately. Clicking
    // Ship opens the ShipPlatformDialog with the idea's primary platform
    // pre-selected (derived from content_type). Only when the operator
    // confirms is the ship endpoint called with the chosen list. Fixes
    // the June-2026 rate-limit issue where a Threads idea over-generated
    // Instagram + other platforms.
    const [shipDialog, setShipDialog] = useState({
        open: false, mode: null, ideaId: null, contentType: null, title: null,
    });

    const openShipDialog = (idea) => {
        setShipDialog({
            open: true, mode: "single",
            ideaId: idea.id, contentType: idea.content_type, title: idea.title,
        });
    };

    const openBulkShipDialog = () => {
        if (selected.size === 0) return;
        // Bulk has mixed content types, so no primary is inferred — the
        // dialog defaults to nothing checked and forces an explicit choice.
        setShipDialog({
            open: true, mode: "bulk",
            ideaId: null, contentType: null, title: null,
            count: selected.size,
        });
    };

    const _pollShipJob = async (jobId, toastId) => {
        const started = Date.now();
        while (true) {
            let job;
            try {
                const r = await api.get(`/content-studio/ship-jobs/${jobId}`);
                job = r.data || {};
            } catch (_) {
                await new Promise((res) => setTimeout(res, 2500));
                if (Date.now() - started > 15 * 60 * 1000) {
                    return { status: "failed", error: "Poll timed out after 15 minutes." };
                }
                continue;
            }
            if (job.status === "running" || job.status === "pending") {
                // Live status text — bulk jobs surface (completed/total); single jobs stay generic.
                const bulk = job.summary && typeof job.summary.completed === "number";
                const label = bulk
                    ? `Shipping ${job.summary.completed}/${job.summary.total}…`
                    : "Shipping — generating + queueing…";
                toast.loading(label, { id: toastId });
                await new Promise((res) => setTimeout(res, 2500));
                if (Date.now() - started > 15 * 60 * 1000) {
                    return { status: "failed", error: "Ship job exceeded 15 minutes." };
                }
                continue;
            }
            return job;
        }
    };

    const shipToBuffer = async (id, platforms) => {
        setShipBusy(id);
        const t = toast.loading("Shipping idea — starting…");
        try {
            const r = await api.post(`/content-studio/idea/${id}/ship`, { platforms: platforms || null });
            const jobId = r.data?.job_id;
            if (!jobId) {
                toast.error("Ship start failed", { id: t });
                return;
            }
            const job = await _pollShipJob(jobId, t);
            if (job.status === "failed") {
                toast.error(`Ship failed: ${job.error || "unknown error"}`, { id: t, duration: 12000 });
                await load();
                return;
            }
            const d = job.summary || {};
            const parts = [`${d.generated || 0} assets generated`];
            if (d.queued) parts.push(`${d.queued} queued to Buffer`);
            if (d.blocked_by_validator) parts.push(`${d.blocked_by_validator} blocked by fact-check`);
            if (d.skipped_media_required) parts.push(`${d.skipped_media_required} skipped — need media`);
            if (d.skipped_rate_limit_cooldown) parts.push(`${d.skipped_rate_limit_cooldown} skipped — in cooldown`);
            const errs = d.queue_errors || [];
            if (errs.length) parts.push(`${errs.length} queue errors`);
            const msg = parts.join(" · ");
            const errDesc = errs.length
                ? errs.slice(0, 2).map((e) => `• ${e.platform || "?"}: ${(e.message || "").slice(0, 220)}`).join("\n")
                  + (errs.length > 2 ? `\n…and ${errs.length - 2} more` : "")
                : "Open the Content Studio Production Line to review.";
            if (d.analysis_warning) {
                toast.warning(d.analysis_warning, { id: t, duration: 9000 });
            } else if (d.queued && !errs.length) {
                toast.success(msg, {
                    id: t, duration: 9000,
                    description: "Open the Content Studio Production Line to review.",
                    action: { label: "Open Studio", onClick: () => { window.location.href = "/content-studio"; } },
                });
            } else {
                toast.warning(msg, { id: t, duration: 14000, description: errDesc });
            }
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Ship to Buffer failed", { id: t });
        } finally {
            setShipBusy(null);
        }
    };

    const bulkShipToBuffer = async (platforms) => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkShipBusy(true);
        const t = toast.loading(`Shipping ${ids.length} idea${ids.length === 1 ? "" : "s"} — starting…`);
        try {
            const r = await api.post("/content-studio/ideas/bulk-ship", {
                idea_ids: ids,
                platforms: platforms || null,
            });
            const jobId = r.data?.job_id;
            if (!jobId) {
                toast.error("Bulk ship start failed", { id: t });
                return;
            }
            const job = await _pollShipJob(jobId, t);
            if (job.status === "failed") {
                toast.error(`Bulk ship failed: ${job.error || "unknown error"}`, { id: t, duration: 12000 });
                await load();
                return;
            }
            const d = (job.summary && job.summary.totals) || {};
            const parts = [
                `${d.generated || 0} assets generated`,
                `${d.queued || 0} queued to Buffer`,
            ];
            if (d.blocked_by_validator) parts.push(`${d.blocked_by_validator} blocked by fact-check`);
            if (d.skipped_media_required) parts.push(`${d.skipped_media_required} skipped — need media`);
            if (d.skipped_rate_limit_cooldown) parts.push(`${d.skipped_rate_limit_cooldown} skipped — in cooldown`);
            if (d.errors) parts.push(`${d.errors} errors`);
            toast.success(parts.join(" · "), {
                id: t, duration: 12000,
                description: "Open the Content Studio Production Line to review.",
                action: { label: "Open Studio", onClick: () => { window.location.href = "/content-studio"; } },
            });
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk ship failed", { id: t });
        } finally {
            setBulkShipBusy(false);
        }
    };

    // Called from the ShipPlatformDialog when the operator confirms
    // their platform selection. Dispatches to single- or bulk-ship
    // depending on which button opened the dialog.
    const handleShipConfirm = async (platforms) => {
        const { mode, ideaId } = shipDialog;
        setShipDialog((s) => ({ ...s, open: false }));
        // Empty array = "ship everything the analyzer recommends" (backend
        // treats null/empty as no-filter).
        const filter = platforms.length ? platforms : null;
        if (mode === "single" && ideaId) {
            await shipToBuffer(ideaId, filter);
        } else if (mode === "bulk") {
            await bulkShipToBuffer(filter);
        }
    };


    const bulkArchive = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkArchiving(true);
        try {
            await Promise.all(ids.map((id) => api.patch(`/content-ideas/${id}`, { status: "Archived" })));
            toast.success(`${ids.length} idea${ids.length === 1 ? "" : "s"} archived`);
            clearSelection();
            await load();
        } catch (err) {
            toast.error("Bulk archive failed");
        } finally {
            setBulkArchiving(false);
        }
    };

    // Phase 5.5: bulk dismiss — uses the same PATCH endpoint as the
    // per-card Dismiss action. Backend stamps dismissed_at on transition.
    const bulkDismiss = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        setBulkDismissing(true);
        try {
            await Promise.all(ids.map((id) => api.patch(`/content-ideas/${id}`, { status: "Dismissed" })));
            toast.success(`${ids.length} idea${ids.length === 1 ? "" : "s"} dismissed`);
            clearSelection();
            await load();
        } catch (err) {
            toast.error("Bulk dismiss failed");
        } finally {
            setBulkDismissing(false);
        }
    };

    // V2.9 — Delete duplicates. First previews the plan (winners kept,
    // losers to delete, blocked losers with Studio variants), then asks
    // the operator to confirm before running the destructive step.
    const [dedupeBusy, setDedupeBusy] = useState(false);
    const deleteDuplicates = async () => {
        setDedupeBusy(true);
        try {
            const preview = await api.post("/content-ideas/dedupe-preview", {});
            const p = preview.data || {};
            if ((p.duplicate_group_count || 0) === 0) {
                toast.success("No duplicates found");
                return;
            }
            const groups = p.groups || [];
            const sampleTitles = groups.slice(0, 3).map((g) => `• ${g.title_prefix || "(untitled)"} (${g.group_size})`).join("\n");
            const more = groups.length > 3 ? `\n…and ${groups.length - 3} more groups` : "";
            const blockedNote = p.blocked_losers
                ? `\n\n${p.blocked_losers} duplicate${p.blocked_losers === 1 ? "" : "s"} will be SKIPPED (already have Content Studio variants).`
                : "";
            const msg = `${p.deletable_losers} duplicate${p.deletable_losers === 1 ? "" : "s"} will be deleted across ${p.duplicate_group_count} group${p.duplicate_group_count === 1 ? "" : "s"}:\n\n${sampleTitles}${more}${blockedNote}\n\nContinue?`;
            if (!window.confirm(msg)) return;
            const r = await api.post("/content-ideas/dedupe", {});
            const d = r.data || {};
            toast.success(`${d.deleted || 0} duplicate${d.deleted === 1 ? "" : "s"} deleted`, {
                description: d.skipped_count
                    ? `${d.skipped_count} skipped (had Studio variants)`
                    : undefined,
                duration: 8000,
            });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete duplicates failed");
        } finally {
            setDedupeBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                overline="Narrative Deployment"
                title="Content Ideas"
                description="Where cultural intelligence becomes communication strategy. Each idea inherits the confidence, resonance, and narrative strength of the insight that inspired it, and moves through a lifecycle from Draft → Review → Approved → In Production → Published."
                testid="content-ideas-header"
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            data-testid="delete-duplicates-btn"
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={deleteDuplicates}
                            disabled={dedupeBusy}
                            title="Scan all Content Ideas and delete duplicate rows. Keeps the strongest copy (highest workflow status, more Studio variants, most recent) in each dup group."
                        >
                            {dedupeBusy
                                ? <><ArrowsClockwise size={14} className="mr-1.5 animate-spin" /> Scanning…</>
                                : <><Trash size={14} weight="bold" className="mr-1.5" /> Delete duplicates</>}
                        </Button>
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-idea-btn" variant="outline" size="sm" className="rounded-sm">
                                <Plus size={14} weight="bold" className="mr-1.5" /> Manual entry
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-xl" data-testid="idea-dialog">
                            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit idea" : "New idea"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <Input data-testid="idea-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" />
                                <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                                    <SelectTrigger data-testid="idea-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                                <Input data-testid="idea-hook-input" placeholder="Hook (the opening line)" value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} className="rounded-sm" />
                                <Input data-testid="idea-insight-input" placeholder="Core insight" value={form.core_insight} onChange={(e) => setForm({ ...form, core_insight: e.target.value })} className="rounded-sm" />
                                <textarea
                                    data-testid="idea-draft-input"
                                    rows={5}
                                    placeholder="Draft copy"
                                    value={form.draft_copy}
                                    onChange={(e) => setForm({ ...form, draft_copy: e.target.value })}
                                    className="w-full rounded-sm border border-input p-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                />
                                <Input data-testid="idea-tags-input" placeholder="topic tags, comma separated" value={form.topic_tags} onChange={(e) => setForm({ ...form, topic_tags: e.target.value })} className="rounded-sm" />
                            </div>
                            <DialogFooter>
                                <Button data-testid="confirm-save-idea" onClick={save} className="rounded-sm">{editing ? "Update" : "Save"}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                }
            />

            <PageBody>
                {/* Lifecycle tabs — the narrative operationalization pipeline */}
                <div data-testid="ideas-pipeline-tabs"
                     className="border-b border-border flex items-end gap-0 mb-4 overflow-x-auto">
                    {/* Phase 5.5: 'Active' (default) collapses the working set;
                        Dismissed + Archived are separate tabs; All shows everything. */}
                    {[
                        { key: "active",    label: "Active" },
                        { key: "Draft",     label: "Draft" },
                        { key: "Review",    label: "Review" },
                        { key: "Approved",  label: "Approved" },
                        { key: "In Production", label: "In Production" },
                        { key: "Published", label: "Published" },
                        { key: "Performing", label: "Performing" },
                        { key: "Dismissed", label: "Dismissed" },
                        { key: "Archived",  label: "Archived" },
                        { key: "all",       label: "All" },
                    ].map((t) => {
                        const active = t.key === activeStatus;
                        // count helper: 'active' = sum of working statuses, 'all' = total
                        const ACTIVE_KEYS = ["Active", "Draft", "Review", "Approved", "In Production"];
                        const countFor = t.key === "active"
                            ? ACTIVE_KEYS.reduce((s, k) => s + (counts[k] || 0), 0)
                            : t.key === "all"
                                ? Object.values(counts).reduce((s, n) => s + (n || 0), 0)
                                : (counts[t.key] || 0);
                        return (
                            <button
                                key={t.key}
                                type="button"
                                data-testid={`ideas-tab-${t.key.replace(/\s+/g, '-').toLowerCase()}`}
                                onClick={() => { setActiveStatus(t.key); localStorage.setItem("haevn-ideas-tab", t.key); }}
                                className={`px-4 py-3 border-b-2 transition-colors text-left ${
                                    active
                                        ? "border-foreground text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className="font-display font-semibold text-sm">{t.label}</span>
                                    <span className={`font-mono tabular-nums text-[11px] ${active ? "text-foreground" : "text-muted-foreground/70"}`}>
                                        {countFor.toString().padStart(2, "0")}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                    <div className="flex-1 border-b-2 border-transparent" />
                </div>

                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                    <p data-testid="active-tab-description" className="font-mono text-[11px] text-muted-foreground tracking-wider">
                        {STATUS_DESCRIPTIONS[activeStatus]
                            || (activeStatus === "active"
                                ? "Working set — Draft, Review, Approved, In Production. Hides Dismissed and Archived."
                                : activeStatus === "all"
                                    ? "All ideas regardless of status — used for audits."
                                    : "")}
                    </p>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="ideas-top-toggle"
                            onClick={() => setTopMode((v) => !v)}
                            className={`text-[11px] font-mono uppercase tracking-[0.16em] border-b pb-0.5 ${topMode ? "border-foreground text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
                            title={topMode
                                ? "Currently showing the 30 ideas with strongest source-insight Narrative Strength. Click to show all."
                                : "Click to filter to the top 30 ideas by source-insight Narrative Strength."}
                        >
                            {topMode ? "Top 30 by strength" : "Show all"}
                        </button>
                        <Select value={filter.content_type || "all"} onValueChange={(v) => setFilter({ ...filter, content_type: v === "all" ? "" : v })}>
                            <SelectTrigger data-testid="filter-type" className="rounded-sm h-8 w-48 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All types</SelectItem>
                                {TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {ideas.length === 0 ? (
                    <EmptyState
                        title={`No ideas in ${activeStatus}`}
                        description={
                            activeStatus === "Draft"
                                ? "Generate ideas from an approved Insight, or create one manually. Each idea will inherit the confidence and resonance of the insight it came from."
                                : `Promote ideas from earlier stages to land them here.`
                        }
                        testid="ideas-empty"
                    />
                ) : (
                    <div data-testid="ideas-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ideas.map((i) => {
                            const ns = i.inherited_narrative_strength || 0;
                            const band = ns > 0 ? getStrengthBand(ns) : null;
                            const isDup = i.is_duplicate;
                            return (
                            <article
                                key={i.id}
                                data-testid={`idea-card-${i.id}`}
                                className={`border bg-card p-5 hover-lift relative ${
                                    selected.has(i.id) ? "border-primary ring-1 ring-primary/30" :
                                    isDup ? "border-amber-300" : "border-border"
                                }`}
                            >
                                <label
                                    className="absolute top-3 right-3 inline-flex items-center cursor-pointer"
                                    title="Select for bulk actions"
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`select-idea-${i.id}`}
                                        checked={selected.has(i.id)}
                                        onChange={() => toggleSelect(i.id)}
                                        className="w-4 h-4 cursor-pointer accent-primary"
                                    />
                                </label>
                                {/* Provenance + strength band */}
                                {(i.source_insight || band) && (
                                    <div data-testid={`idea-provenance-${i.id}`} className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-border">
                                        {i.source_insight ? (
                                            <Link
                                                to={`/insights?highlight=${i.source_insight.id}`}
                                                className="flex items-start gap-2 text-[11px] text-muted-foreground hover:text-primary transition-colors min-w-0"
                                            >
                                                <Lightbulb size={11} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                                                <span className="line-clamp-2 italic leading-snug">Inspired by: {i.source_insight.title}</span>
                                            </Link>
                                        ) : (
                                            <span className="text-[11px] italic text-muted-foreground">No source insight</span>
                                        )}
                                        {band && (
                                            <span
                                                className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm ${band.cls}`}
                                                title={`Inherited Narrative Strength ${ns}/100 (${band.label}). The strategic strength of this idea is anchored to its source insight.`}
                                            >
                                                NS {ns} · {band.label}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="rounded-sm font-mono text-[10px] tracking-wider">{i.content_type}</Badge>
                                        {i.structure && (
                                            <span
                                                className="font-mono text-[10px] tracking-wider text-primary border border-primary/30 px-1.5 py-0.5 rounded-sm"
                                                title={`Structure ${i.structure} — content engine rotates A (Observation→Reframe→POV), B (Contrarian), C (Founder), D (Behavioral), E (Myth-bust), F (Story-led).`}
                                            >
                                                STR · {i.structure}
                                            </span>
                                        )}
                                        {i.mentions_haevn && (
                                            <span
                                                className="font-mono text-[10px] tracking-wider text-accent border border-accent/40 px-1.5 py-0.5 rounded-sm"
                                                title="This idea names HAEVN directly. Target is ~20–30% of ideas."
                                            >
                                                HAEVN
                                            </span>
                                        )}
                                        {isDup && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-700"
                                                title={`This idea appears similar to ${i.duplicate_group_size - 1} other${i.duplicate_group_size > 2 ? "s" : ""} in the same status — consider consolidating or archiving the duplicates.`}
                                            >
                                                <Warning size={11} weight="duotone" /> dup
                                            </span>
                                        )}
                                    </div>
                                    <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                                        <SelectTrigger data-testid={`idea-status-${i.id}`} className="rounded-sm h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <h3 className="font-display font-bold text-base leading-tight">{i.title}</h3>
                                {i.hook && <p className="mt-3 text-sm italic text-foreground/90">&ldquo;{i.hook}&rdquo;</p>}
                                {i.core_insight && <p className="mt-2 text-xs text-muted-foreground">{i.core_insight}</p>}
                                {i.quality_scores && (
                                    <div
                                        className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[10px] font-mono text-muted-foreground"
                                        data-testid={`idea-voice-scores-${i.id}`}
                                        title="Content engine quality scores (0–100). HV: Human Voice · CR: Credibility · SP: Specificity · TN: Tension · FC: Founder credibility · CP: Conversation potential · BF: Brand fit · OR: Originality (vs recent ideas) · CT: Content-type schema accuracy."
                                    >
                                        {[
                                            ["HV", i.quality_scores.human_voice],
                                            ["CR", i.quality_scores.credibility],
                                            ["SP", i.quality_scores.specificity],
                                            ["TN", i.quality_scores.tension],
                                            ["FC", i.quality_scores.founder_credibility],
                                            ["CP", i.quality_scores.conversation_potential],
                                            ["BF", i.quality_scores.brand_fit],
                                            ["OR", i.quality_scores.originality],
                                            ["CT", i.quality_scores.content_type_accuracy],
                                        ].filter(([, v]) => v !== undefined && v !== null).map(([label, v]) => (
                                            <span key={label} className="flex items-baseline gap-1">
                                                <span className="overline text-[9px] tracking-[0.14em]">{label}</span>
                                                <span className={`tabular-nums ${v >= 70 ? "text-emerald-700" : v >= 45 ? "text-foreground" : "text-accent"}`}>{v}</span>
                                            </span>
                                        ))}
                                        {(i.quality_scores.banned_phrases || []).length > 0 && (
                                            <span className="text-accent italic">⚠ {i.quality_scores.banned_phrases.length} banned</span>
                                        )}
                                    </div>
                                )}
                                {i.content_type === "Carousel" && (i.slides || []).length > 0 && (
                                    <ol className="mt-3 space-y-1.5" data-testid={`idea-slides-${i.id}`}>
                                        {i.slides.map((s, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-xs">
                                                <span className="font-mono text-[10px] text-primary tabular-nums shrink-0 mt-0.5">{String(idx + 1).padStart(2, "0")}</span>
                                                <span className="text-foreground/90 leading-snug">{s}</span>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                                {i.draft_copy && (i.content_type !== "Carousel" || (i.slides || []).length === 0) && (
                                    <pre className="mt-3 whitespace-pre-wrap font-sans text-xs text-muted-foreground line-clamp-6 leading-relaxed">{i.draft_copy}</pre>
                                )}
                                {/* How to use this idea */}
                                {(i.source_stream || (i.topic_tags || []).length > 0) && (
                                    <details className="mt-4 pt-3 border-t border-border" data-testid={`idea-howto-${i.id}`}>
                                        <summary className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary cursor-pointer select-none">
                                            How to use this
                                        </summary>
                                        <div className="mt-3 text-[12px] text-muted-foreground space-y-2">
                                            {i.source_stream && (
                                                <div><span className="font-mono uppercase text-[10px] tracking-wider mr-2">Stream</span>{i.source_stream.name}</div>
                                            )}
                                            {i.source_insight?.emotional_theme && (
                                                <div><span className="font-mono uppercase text-[10px] tracking-wider mr-2">Theme</span>{i.source_insight.emotional_theme}</div>
                                            )}
                                            {(i.topic_tags || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {i.topic_tags.map((t) => (
                                                        <span key={t} className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded-sm">{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {ns > 0 && (
                                                <p className="italic leading-snug text-foreground/70">
                                                    Best deployed while the source narrative remains in <strong>{band?.label}</strong> territory.
                                                </p>
                                            )}
                                        </div>
                                    </details>
                                )}

                                {/* Intelligence provenance — shows where this idea's credibility comes from */}
                                {(i.related_insight || i.related_analysis || i.related_hypothesis_id || i.narrative_strength > 0) && (
                                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-border/60 text-[10px] font-mono text-muted-foreground">
                                        {i.narrative_strength > 0 && (
                                            <span className="flex items-baseline gap-1.5">
                                                <span className="overline text-[9px] tracking-[0.16em]">strength</span>
                                                <span className="text-foreground tabular-nums">{i.narrative_strength}</span>
                                            </span>
                                        )}
                                        {i.confidence && i.confidence !== "low" && (
                                            <span className="flex items-baseline gap-1.5">
                                                <span className="overline text-[9px] tracking-[0.16em]">conf</span>
                                                <span className="text-foreground">{i.confidence}</span>
                                            </span>
                                        )}
                                        {i.resonance && i.resonance !== "low" && (
                                            <span className="flex items-baseline gap-1.5">
                                                <span className="overline text-[9px] tracking-[0.16em]">resonance</span>
                                                <span className="text-accent">{i.resonance}</span>
                                            </span>
                                        )}
                                        {i.related_insight && <span className="text-muted-foreground/80">· from insight</span>}
                                        {i.related_hypothesis_id && <span className="text-muted-foreground/80">· from hypothesis</span>}
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-1 mt-3">
                                    {(i.topic_tags || []).slice(0, 4).map((t) => (
                                        <span key={t} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm">{t}</span>
                                    ))}
                                </div>
                                <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-border items-center">
                                    {/* Phase 5.5: 'In Studio' badge — replaces the proposed
                                        decorative badge with one that's actually clickable. */}
                                    {(i.studio_variants_count || 0) > 0 && (
                                        <button
                                            type="button"
                                            data-testid={`in-studio-${i.id}`}
                                            onClick={() => openStudioForIdea(i.id)}
                                            className="mr-auto inline-flex items-center gap-1 px-1.5 h-6 rounded-sm border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition-colors font-mono text-[10px] uppercase tracking-wider"
                                            title={`${i.studio_variants_count} Content Studio variant${i.studio_variants_count === 1 ? "" : "s"} created from this idea — click to open`}
                                        >
                                            <FilmStrip size={11} weight="duotone" />
                                            In Studio · {i.studio_variants_count >= 3 ? "3+" : i.studio_variants_count}
                                        </button>
                                    )}
                                    <Button
                                        data-testid={`ship-to-buffer-${i.id}`}
                                        size="sm"
                                        className="rounded-sm h-7 px-2 text-[10px] font-mono uppercase tracking-wider"
                                        onClick={() => openShipDialog(i)}
                                        disabled={shipBusy === i.id}
                                        title="Ship this idea — pick which platforms to publish to (primary platform pre-selected), fact-checked and queued to Buffer."
                                    >
                                        <Rocket size={12} weight="fill" className="mr-1" />
                                        {shipBusy === i.id ? "Shipping…" : "Ship to Buffer"}
                                    </Button>
                                    <Button
                                        data-testid={`send-to-studio-${i.id}`}
                                        variant="ghost" size="sm"
                                        className="rounded-sm h-7 px-2 text-[10px] font-mono uppercase tracking-wider text-primary hover:text-primary hover:bg-primary/10"
                                        onClick={() => sendToStudio(i.id)}
                                        disabled={sendBusy === i.id}
                                        title="Create a Content Studio Source from this idea — gives you the editor to choose platforms manually instead of one-click shipping."
                                    >
                                        <FilmStrip size={12} weight="duotone" className="mr-1" />
                                        {sendBusy === i.id ? "Sending…" : "Send to Studio"}
                                    </Button>
                                    <Button
                                        data-testid={`regen-voice-${i.id}`}
                                        variant="ghost" size="sm"
                                        className="rounded-sm h-7 px-2 text-[10px] font-mono uppercase tracking-wider text-primary hover:text-primary hover:bg-primary/10"
                                        onClick={() => regenVoice(i.id)}
                                        disabled={regenBusy === i.id}
                                        title="Rewrite this idea with stronger HAEVN voice — sharper opinion, more specific behavior, more friction. Preserves the source insight and content type."
                                    >
                                        <MagicWand size={12} weight="duotone" className="mr-1" />
                                        {regenBusy === i.id ? "Regenerating…" : "Stronger voice"}
                                    </Button>
                                    <Button data-testid={`edit-idea-${i.id}`} variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => startEdit(i)} title="Edit this idea — change title, hook, copy, or content type"><Pencil size={12} /></Button>
                                    <Button data-testid={`dup-idea-${i.id}`} variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => dup(i.id)} title="Duplicate — clone this idea as a new Draft so you can riff on it"><Copy size={12} /></Button>
                                    <Button
                                        data-testid={`dismiss-idea-${i.id}`}
                                        variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0"
                                        onClick={() => updateStatus(i.id, "Dismissed")}
                                        title="Dismiss — remove from your working view (off-brand, duplicate, weak). Preserves historical intelligence."
                                    ><Prohibit size={12} /></Button>
                                    <Button data-testid={`archive-idea-${i.id}`} variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => updateStatus(i.id, "Archived")} title="Archive — move to Archived (used, completed, or intentionally preserved)"><Archive size={12} /></Button>
                                    {isAdmin && (
                                    <Button
                                        data-testid={`delete-idea-${i.id}`}
                                        variant="ghost" size="sm"
                                        className="rounded-sm h-7 w-7 p-0"
                                        onClick={() => remove(i.id)}
                                        disabled={(i.studio_variants_count || 0) > 0}
                                        title={(i.studio_variants_count || 0) > 0
                                            ? `Delete disabled — ${i.studio_variants_count} Studio variant${i.studio_variants_count === 1 ? "" : "s"} ${i.studio_variants_count === 1 ? "exists" : "exist"}. Dismiss or Archive instead to preserve history.`
                                            : "Delete permanently (admin only) — only available when no Studio variants exist. Cannot be undone."}
                                    ><Trash size={12} /></Button>
                                    )}
                                </div>
                            </article>
                            );
                        })}
                    </div>
                )}
            </PageBody>

            {/* Sticky bulk-actions bar — appears when one or more cards are selected. */}
            {selected.size > 0 && (
                <div
                    data-testid="bulk-actions-bar"
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-foreground text-background shadow-2xl rounded-sm border border-foreground/20 px-4 py-2.5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider"
                >
                    <span className="tabular-nums" data-testid="bulk-count">
                        {selected.size} selected
                    </span>
                    <button
                        onClick={selectAllVisible}
                        className="text-background/70 hover:text-background normal-case tracking-normal text-[11px]"
                        data-testid="bulk-select-all"
                    >
                        Select all visible
                    </button>
                    <span className="w-px h-4 bg-background/30" />
                    <Button
                        data-testid="bulk-ship-to-buffer"
                        size="sm"
                        className="rounded-sm h-7 px-3 text-[10px] font-mono uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={openBulkShipDialog}
                        disabled={bulkShipBusy}
                    >
                        <Rocket size={12} weight="fill" className="mr-1" />
                        {bulkShipBusy ? "Shipping…" : `Ship ${selected.size} to Buffer`}
                    </Button>
                    <Button
                        data-testid="bulk-send-to-studio"
                        size="sm" variant="secondary"
                        className="rounded-sm h-7 px-3 text-[10px] font-mono uppercase tracking-wider"
                        onClick={() => bulkSendToStudio()}
                        disabled={bulkBusy}
                    >
                        <FilmStrip size={12} weight="duotone" className="mr-1" />
                        {bulkBusy ? "Sending…" : "Send to Studio (no auto-ship)"}
                    </Button>
                    <Button
                        data-testid="bulk-dismiss"
                        size="sm" variant="ghost"
                        className="rounded-sm h-7 px-3 text-[10px] font-mono uppercase tracking-wider text-background hover:bg-background/10"
                        onClick={bulkDismiss}
                        disabled={bulkDismissing}
                        title="Dismiss selected — mark as off-brand, duplicate, or weak. Preserves historical intelligence."
                    >
                        <Prohibit size={12} className="mr-1" />
                        {bulkDismissing ? "Dismissing…" : "Dismiss selected"}
                    </Button>
                    <Button
                        data-testid="bulk-archive"
                        size="sm" variant="ghost"
                        className="rounded-sm h-7 px-3 text-[10px] font-mono uppercase tracking-wider text-background hover:bg-background/10"
                        onClick={bulkArchive}
                        disabled={bulkArchiving}
                    >
                        <Archive size={12} className="mr-1" />
                        {bulkArchiving ? "Archiving…" : "Archive selected"}
                    </Button>
                    <button
                        onClick={clearSelection}
                        className="text-background/70 hover:text-background normal-case tracking-normal text-[11px] ml-1"
                        data-testid="bulk-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* Duplicate-prevention modal — shows when the API returns 409 for a single send. */}
            <Dialog open={!!dupModal} onOpenChange={(o) => { if (!o) setDupModal(null); }}>
                <DialogContent className="rounded-sm max-w-md" data-testid="dup-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display">This idea already exists in Content Studio.</DialogTitle>
                        <DialogDescription className="text-[12px] text-muted-foreground">
                            A Studio piece was already created from this Content Idea. Open it to keep editing, or create another copy if you want a fresh variant.
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">
                        A Studio piece called <span className="text-foreground italic">&ldquo;{dupModal?.existing_title}&rdquo;</span> was already created from this idea. You can open it, or create another copy if you want a fresh variant.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button
                            data-testid="dup-create-copy"
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => {
                                const id = dupModal.idea_id;
                                setDupModal(null);
                                sendToStudio(id, { force: true });
                            }}
                        >
                            Create another copy
                        </Button>
                        <Button
                            data-testid="dup-open-existing"
                            className="rounded-sm"
                            onClick={() => {
                                openExisting(dupModal.existing_content_id);
                                setDupModal(null);
                            }}
                        >
                            Open existing content
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ShipPlatformDialog
                open={shipDialog.open}
                onOpenChange={(v) => setShipDialog((s) => ({ ...s, open: v }))}
                onConfirm={handleShipConfirm}
                contentType={shipDialog.contentType}
                title={shipDialog.title}
                countLabel={shipDialog.mode === "bulk" ? `${shipDialog.count} ideas` : null}
                busy={shipDialog.mode === "bulk" ? bulkShipBusy : shipBusy === shipDialog.ideaId}
            />
        </>
    );
}
